import { FC, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Button, ValidationForm } from '../../atoms';

import { CustomDropdown } from '../../molecules';
import BaseTemplate from '../../templates/BaseTemplate';
import { Container } from 'react-bootstrap';
import styles from './CommoditiesEditChooseOrganism.module.css';
import { LandscapeCommodityResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-commodity-response.dto';
import { LandscapeCommodityAvailabilityType } from '../../../store/landscapeAPI/landscape/landscape-commodity-availability-type';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchCommoditiesNonRecursive } from '../../../store/tradeAPI/commodity/reducers/commodity.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { NonRecursiveCommodityResponseDto } from '../../../store/tradeAPI/commodity/dto/response/non-recursive-commodity-response.dto';

interface CommoditiesEditChooseOrganismProps {
  commodityLandscape: LandscapeCommodityResponseDto;
  handleOnChange: (commodity: LandscapeCommodityResponseDto) => void;
  callbackFn: (commodityLandscape: LandscapeCommodityResponseDto) => void;
}

interface DropDownItem {
  eventKey: string;
  label: string;
}

const CommoditiesEditChooseOrganism: FC<CommoditiesEditChooseOrganismProps> = ({
  commodityLandscape,
  handleOnChange,
  callbackFn,
}) => {
  const { nonRecursiveCommodities: commodities, nonRecursiveStatus: status } =
    useSelector((state: RootState) => state.commoditySlice);

  const [commodityItems, setCommodityItems] = useState<DropDownItem[]>();
  const [commodityTitle, setCommodityTitle] = useState<string>();

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(fetchCommoditiesNonRecursive());
  }, [dispatch]);

  useEffect(() => {
    if (status === 'succeeded' && commodities) {
      setCommodityItems(
        commodities
          ? commodities.map((commodity: NonRecursiveCommodityResponseDto) => ({
              eventKey: commodity.id,
              label: commodity.name,
            }))
          : []
      );
    }
  }, [commodities, status]);

  useEffect(() => {
    if (commodityItems) {
      setCommodityTitle(
        commodityItems.find(
          (commodityItem) =>
            commodityItem.eventKey === commodityLandscape.commodityId
        )?.label ?? 'Choose commodity'
      );
    }
  }, [commodityItems, commodityLandscape.commodityId]);

  const availabilityTypes = [
    LandscapeCommodityAvailabilityType.Available,
    LandscapeCommodityAvailabilityType.NotAvailable,
  ];

  const availabilityItems = availabilityTypes.map((type) => ({
    eventKey: type,
    label: type.toString(),
  }));

  const availabilityInitialTitle = availabilityItems.find(
    (availabilityItem) =>
      availabilityItem.eventKey === commodityLandscape.availabilityType
  )?.label;

  if (status === 'loading' || !commodityTitle) {
    return <Spinner />;
  }

  return (
    <BaseTemplate>
      <Container className={styles.container}>
        <ValidationForm onSubmitCallback={() => callbackFn(commodityLandscape)}>
          <Form.Group className="mb-3">
            <Form.Label>Commodity</Form.Label>
            <CustomDropdown
              initialTitle={commodityTitle}
              items={commodityItems ?? []}
              onSelectOption={(e) => {
                handleOnChange({
                  ...commodityLandscape,
                  commodityId: e.eventKey,
                  name: e.label,
                });
              }}
              disabled
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              onChange={(e) =>
                handleOnChange({
                  ...commodityLandscape,
                  qty: Number(e.target.value),
                })
              }
              defaultValue={commodityLandscape.qty ?? ''}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Time</Form.Label>
            <Form.Control
              type="number"
              onChange={(e) =>
                handleOnChange({
                  ...commodityLandscape,
                  time: Number(e.target.value),
                })
              }
              defaultValue={commodityLandscape.time ?? ''}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Availability</Form.Label>
            <CustomDropdown
              initialTitle={
                availabilityInitialTitle ??
                LandscapeCommodityAvailabilityType.Available
              }
              items={availabilityItems}
              onSelectOption={(item) => {
                const selectedType = availabilityTypes?.find(
                  (type) => type === item.eventKey
                )!;
                handleOnChange({
                  ...commodityLandscape,
                  availabilityType: selectedType,
                });
              }}
            />
          </Form.Group>

          <Button variant="dark" type="submit">
            Save
          </Button>
        </ValidationForm>
      </Container>
    </BaseTemplate>
  );
};

export default CommoditiesEditChooseOrganism;
